import PageContainer from "../../components/page-container";
import Button from "../../components/button/button";
import EnvironmentPicker from "../../components/environment-picker";
import { useCallback, useEffect, useState } from "react";
import { forceSync, getAnalyticsReport, getSyncStatus } from "../../services/analytics";
import { useAppInfo } from "../../helpers/hooks/common-hook";
import { useAppEnvironment } from "../../helpers/hooks/app-environment-hook";
import {
    HiOutlineArrowPath,
    HiOutlineChatBubbleLeftRight,
    HiOutlineCheckCircle,
    HiOutlineExclamationTriangle,
    HiOutlineUserGroup,
} from "react-icons/hi2";
import {
    CARD,
    CategoryPanel,
    EmptyState,
    IssueCard,
    Kpi,
    KpiSkeleton,
    LAST_30,
    LastUpdated,
    Notice,
    OutcomeBar,
    RangePicker,
    SectionSkeleton,
    Spinner,
    TopicPanel,
    rangeParam,
} from "./components";

const SYNC_POLL_INTERVAL = 5000;
const SYNC_MAX_WAIT_MS = 30 * 60 * 1000;

function plural(count, word) {
    return `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;
}

export default function Analytics() {
    const { appId } = useAppInfo();
    const { env, setEnv } = useAppEnvironment(appId);
    const envLabel = env === 'live' ? 'Live' : 'Test';
    const [rangeKey, setRangeKey] = useState(LAST_30);
    const [rangeLabel, setRangeLabel] = useState('Last 30 days');
    const [computedRanges, setComputedRanges] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sync, setSync] = useState({ running: false, message: '', startedAt: null });

    const loadReport = useCallback(async (key = rangeKey) => {
        const { response, status } = await getAnalyticsReport(appId, rangeParam(key), env);
        if (status !== 200) {
            setError(typeof response === 'string' && response ? response : 'Could not load analytics. Please try again.');
            setLoading(false);
            return null;
        }
        const result = response?.result || {};
        // A backend without range or environment support answers every request
        // with the Test, last-30-days report. Showing it under another name
        // would be wrong data, so refuse it instead.
        const servedKey = result.range?.key || LAST_30;
        if (servedKey !== key || (result.env || 'test') !== env) {
            setError('This period cannot be loaded yet: the analytics service needs updating.');
            setReport(null);
            setLoading(false);
            return null;
        }
        setError('');
        setReport(result.report || null);
        setRangeLabel(result.range?.label || 'Last 30 days');
        setComputedRanges(result.computedRanges || []);
        setLoading(false);
        return result.sync || null;
    }, [appId, rangeKey, env]);

    // The run happens in the backend, not in this tab: reloading or leaving
    // never stops it. The page just follows whatever is running for the app.
    const watchSync = useCallback(async running => {
        setSync({ running: true, message: '', startedAt: running?.startedAt ?? null, label: running?.rangeLabel });
        const deadline = Date.now() + SYNC_MAX_WAIT_MS;
        while (Date.now() < deadline) {
            await new Promise(resolve => setTimeout(resolve, SYNC_POLL_INTERVAL));
            const { response, status } = await getSyncStatus(appId, env);
            if (status !== 200) continue;
            const current = response?.result;
            if (current && !current.running) {
                await loadReport();
                setSync({
                    running: false,
                    startedAt: null,
                    message: current.status === 'failed'
                        ? `Analysis failed: ${current.message || 'unknown error'}`
                        : '',
                });
                return;
            }
        }
        setSync({ running: false, startedAt: null, message: 'Analysis is taking longer than expected. Reload to check on it.' });
    }, [appId, env, loadReport]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setReport(null);
        (async () => {
            const current = await loadReport(rangeKey);
            if (!cancelled && current?.running) {
                watchSync(current);
            }
        })();
        return () => { cancelled = true; };
        // Reload only when the app, environment or range changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, rangeKey, env]);

    async function handleForceSync() {
        setSync({ running: true, message: '', startedAt: null, label: rangeLabel });
        const { response, status } = await forceSync(appId, rangeParam(rangeKey), env);
        const result = response?.result;
        if (status === 200 && (result?.triggered || result?.reason === 'already-running')) {
            await watchSync(result.sync);
            return;
        }
        setSync({
            running: false,
            startedAt: null,
            message: result?.reason || (typeof response === 'string' ? response : '') || 'Could not start analysis. Please try again.',
        });
    }

    const totals = report?.totals;
    const hasAnalysed = (totals?.analysed || 0) > 0;
    const rate = value => (hasAnalysed ? `${value}%` : '—');
    const rolling = rangeKey === LAST_30;
    const untraced = hasAnalysed && typeof totals.traced === 'number' && totals.traced < totals.analysed;
    const issues = report?.issues || [];

    const analyseButton = (
        <Button
            color="primary"
            size="sm"
            text={sync.running ? 'Analysing…' : 'Analyse now'}
            loading={sync.running}
            disabled={sync.running}
            icon={<HiOutlineArrowPath className="h-4 w-4" />}
            onClick={handleForceSync}
        />
    );

    return (
        <PageContainer
            title="Analytics"
            subtitle="Is your agent working, why does it fail, and what to fix first"
            headerAction={analyseButton}
        >
            {/* Toolbar: what is shown, and how fresh it is. */}
            <div className={`${CARD} mb-4 flex flex-wrap items-center justify-between gap-3 px-3 py-2.5`}>
                <div className="flex flex-wrap items-center gap-2">
                    <EnvironmentPicker value={env} onChange={setEnv} disabled={loading} />
                    <span className="hidden h-6 w-px bg-gray-200 sm:block" aria-hidden="true" />
                    <RangePicker rangeKey={rangeKey} computedRanges={computedRanges} disabled={loading} onChange={setRangeKey} />
                </div>
                {sync.running ? (
                    <span className="inline-flex items-center gap-2 text-xs font-medium text-sky-700 dark:text-sky-300">
                        <Spinner /> Analysing {sync.label || rangeLabel} ({envLabel})
                    </span>
                ) : (
                    report && <LastUpdated at={report.computedAt} rolling={rolling} />
                )}
            </div>

            <div className="mb-4 space-y-2 empty:hidden">
                {sync.running && (
                    <Notice tone="info">You can leave this page; the report updates when the analysis finishes.</Notice>
                )}
                {!sync.running && sync.message && <Notice tone="quiet">{sync.message}</Notice>}
                {error && (
                    <Notice tone="error" action={
                        <button type="button" onClick={() => loadReport()} className="font-semibold underline hover:no-underline">Retry</button>
                    }>{error}</Notice>
                )}
                {totals?.inProgress > 0 && (
                    <Notice tone="info">
                        {plural(totals.inProgress, 'conversation')} still active; analysed once {totals.inProgress === 1 ? 'it ends' : 'they end'}.
                    </Notice>
                )}
                {totals?.pending > 0 && (
                    <Notice tone="warn">
                        {plural(totals.pending, 'conversation')} not analysed yet.{' '}
                        {totals.dailyLimitReached
                            ? 'Daily limit reached; the rest are picked up tomorrow.'
                            : 'Figures cover what has been analysed so far.'}
                    </Notice>
                )}
                {untraced && (
                    <Notice tone="quiet">Conversations older than 30 days are analysed from the transcript only.</Notice>
                )}
            </div>

            {loading ? (
                <>
                    <dl className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
                        <KpiSkeleton /><KpiSkeleton /><KpiSkeleton /><KpiSkeleton />
                    </dl>
                    <SectionSkeleton />
                </>
            ) : !report ? (
                !error && (
                    <EmptyState
                        title={sync.running ? `Analysing ${rangeLabel}…` : `${rangeLabel} (${envLabel}) has not been analysed yet`}
                        body={sync.running
                            ? 'Each conversation is reviewed for its outcome and, when it went wrong, the cause in your agent.'
                            : 'Run an analysis to see how conversations ended, why they failed, and what to fix first.'}
                        action={!sync.running && analyseButton}
                    />
                )
            ) : (
                <div className="space-y-6 pb-10">
                    <dl className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
                        <Kpi
                            label="Conversations"
                            value={totals.conversations.toLocaleString()}
                            detail={`${totals.analysed.toLocaleString()} analysed${totals.noRequest ? ` · ${totals.noRequest} greetings only` : ''}`}
                            icon={HiOutlineChatBubbleLeftRight}
                        />
                        <Kpi
                            label="Resolved"
                            value={rate(totals.resolutionRate)}
                            meter={hasAnalysed ? totals.resolutionRate : undefined}
                            detail={hasAnalysed ? `${totals.resolved} of the in-scope conversations` : 'No conversations analysed yet'}
                            icon={HiOutlineCheckCircle}
                        />
                        <Kpi
                            label="Escalated to a person"
                            value={`${totals.escalationRate}%`}
                            meter={totals.escalationRate}
                            detail={plural(totals.escalated, 'conversation')}
                            icon={HiOutlineUserGroup}
                        />
                        <Kpi
                            label="Had a problem"
                            value={rate(totals.issueRate)}
                            meter={hasAnalysed ? totals.issueRate : undefined}
                            detail={hasAnalysed ? `${totals.withIssues} of ${totals.analysed} analysed` : 'No conversations analysed yet'}
                            icon={HiOutlineExclamationTriangle}
                        />
                    </dl>

                    <OutcomeBar outcomes={report.outcomes} />

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <CategoryPanel categories={report.categories} />
                        <TopicPanel topics={report.topics} />
                    </div>

                    <section>
                        <div className="flex flex-wrap items-end justify-between gap-2">
                            <div>
                                <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                                    What to fix first
                                    {issues.length > 0 && (
                                        <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[11px] font-semibold text-white">
                                            {issues.length}
                                        </span>
                                    )}
                                </h2>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    Ranked by how many conversations each problem affects and how badly. Open an example for the full conversation and trace.
                                </p>
                            </div>
                        </div>
                        {issues.length === 0 ? (
                            <div className="mt-4">
                                <EmptyState title="Nothing to fix" body="No problems were found in the analysed conversations." />
                            </div>
                        ) : (
                            <ol className="mt-4 space-y-3">
                                {issues.map((issue, index) => (
                                    <IssueCard key={issue.id} issue={issue} appId={appId} env={env} rank={index + 1} />
                                ))}
                            </ol>
                        )}
                    </section>
                </div>
            )}
        </PageContainer>
    );
}
